import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const NavItem = ({ id, label }: { id: string; label: string }) => {
    if (isHome) {
      return (
        <button
          onClick={() => scrollToSection(id)}
          className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          {label}
        </button>
      );
    }
    return (
      <Link
        to={`/#${id}`}
        onClick={() => setMobileOpen(false)}
        className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
      >
        {label}
      </Link>
    );
  };

  const MobileNavItem = ({ id, label }: { id: string; label: string }) => {
    if (isHome) {
      return (
        <button
          onClick={() => scrollToSection(id)}
          className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 hover:text-primary-500 transition-colors text-left cursor-pointer whitespace-nowrap w-full"
        >
          <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
          {label}
        </button>
      );
    }
    return (
      <Link
        to={`/#${id}`}
        onClick={() => setMobileOpen(false)}
        className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 hover:text-primary-500 transition-colors text-left cursor-pointer whitespace-nowrap w-full"
      >
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
        {label}
      </Link>
    );
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading font-semibold text-foreground-900 text-base md:text-lg whitespace-nowrap">
              Vigorbuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <NavItem id="categories" label="Categories" />
            <NavItem id="features" label="Features" />
            <Link to="/article/vigorbuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
              Shopping Guide
            </Link>
            <NavItem id="faq" label="FAQ" />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Explore Now
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-sm font-semibold text-primary-500 py-2.5 cursor-pointer whitespace-nowrap w-full"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </Link>

          <MobileNavItem id="categories" label="Categories" />
          <MobileNavItem id="features" label="Features" />

          <Link
            to="/article/vigorbuy-spreadsheet-guide"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground-700 py-2.5 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap w-full"
          >
            <i className="ri-book-open-line text-base"></i>
            Shopping Guide
          </Link>

          <MobileNavItem id="faq" label="FAQ" />

          <div className="border-t border-background-200/70 my-2 pt-2">
            <button
              onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
              className="flex items-center justify-between text-sm font-semibold text-foreground-700 py-2.5 cursor-pointer whitespace-nowrap w-full"
            >
              <span className="flex items-center gap-2">
                <i className="ri-price-tag-3-line text-base"></i>
                Browse Categories
              </span>
              <i className={mobileCatsOpen ? 'ri-arrow-up-s-line text-foreground-400' : 'ri-arrow-down-s-line text-foreground-400'}></i>
            </button>

            {mobileCatsOpen && (
              <div className="pl-4 py-1 space-y-1 border-l-2 border-background-200/70 ml-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between text-sm text-foreground-600 py-2 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap w-full"
                  >
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 mt-2 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Explore Now
            <i className="ri-arrow-right-up-line text-xs"></i>
          </a>
        </div>
      )}
    </nav>
  );
}